import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  imports,
  NEEDS_ORG_SELECTOR,
  type ImportKind,
  type ContractorImportRow,
  type NomenclatureImportRow,
  type DepartmentImportRow,
  type PositionImportRow,
  type EmployeeImportRow,
  type ImportResult,
} from '@/api/openingBalances';
import { orgs } from '@/api/catalogs';
import { humanizeBackendError } from '@/utils/backendError';

// Б.5: profession_classifier ВИДАЛЕНО з UI (Б.4: глобальний КП-довідник,
// tenant-write заборонено навічно; backend 422 завжди). Якщо хтось
// додасть назад — drift-guard у тесті спіймає.
type Kind = ImportKind;

function parseCsv(text: string): string[][] {
  const out: string[][] = [];
  const lines = text.split(/\r?\n/);
  const firstLine = lines[0] ?? '';
  const semi = (firstLine.match(/;/g) ?? []).length;
  const comma = (firstLine.match(/,/g) ?? []).length;
  const sep = semi > comma ? ';' : ',';
  for (const line of lines) {
    if (line === '') continue;
    const row: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]!;
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { cur += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === sep) { row.push(cur); cur = ''; }
        else { cur += ch; }
      }
    }
    row.push(cur);
    out.push(row);
  }
  return out;
}

interface ColDef {
  key: string;
  label: string;
  required?: boolean;
}

const COLS: Record<Kind, ColDef[]> = {
  contractors: [
    { key: 'external_id', label: 'ID з 1С (external_id)' },
    { key: 'name', label: 'Назва', required: true },
    { key: 'full_name', label: 'Повна назва' },
    { key: 'code', label: 'Код' },
    { key: 'edrpou', label: 'ЄДРПОУ' },
    { key: 'inn', label: 'ІПН' },
    { key: 'contractor_type', label: 'Тип (LEGAL/INDIVIDUAL_FOP/INDIVIDUAL)' },
    { key: 'is_vat_payer', label: 'Платник ПДВ (true/false)' },
    { key: 'vat_number', label: 'Номер ПДВ' },
  ],
  nomenclature: [
    { key: 'external_id', label: 'ID з 1С' },
    { key: 'name', label: 'Назва', required: true },
    { key: 'full_name', label: 'Повна назва' },
    { key: 'code', label: 'Код' },
    { key: 'article', label: 'Артикул' },
    { key: 'item_type', label: 'Тип (GOOD/SERVICE)' },
  ],
  departments: [
    { key: 'external_id', label: 'ID з 1С' },
    { key: 'code', label: 'Код', required: true },
    { key: 'name', label: 'Назва', required: true },
    { key: 'parent_external_id', label: 'ID батьківського відділу (для ієрархії)' },
  ],
  positions: [
    { key: 'external_id', label: 'ID з 1С' },
    { key: 'code', label: 'Код', required: true },
    { key: 'name', label: 'Назва', required: true },
    { key: 'kp_code', label: 'КП код (ДК 003:2010)' },
  ],
  employees: [
    { key: 'external_id', label: 'ID з 1С' },
    { key: 'last_name', label: 'Прізвище', required: true },
    { key: 'first_name', label: "Ім'я", required: true },
    { key: 'middle_name', label: 'По батькові' },
    { key: 'inn', label: 'ІПН (12 цифр)' },
    { key: 'code', label: 'Табельний номер' },
    { key: 'position_external_id', label: 'ID посади з 1С' },
    { key: 'department_external_id', label: 'ID відділу з 1С' },
    { key: 'hire_date', label: 'Дата прийому' },
  ],
};

const KIND_LABELS: Record<Kind, string> = {
  contractors: 'Контрагенти',
  nomenclature: 'Номенклатура',
  departments: 'Відділи',
  positions: 'Посади',
  employees: 'Співробітники (в розробці)',
};

const SOURCE_OPTIONS = [
  { value: '1C_BP', label: '1С: БП для України' },
  { value: '1C_UTP', label: '1С: УТП для України' },
  { value: '1C_UT', label: '1С: УТ' },
  { value: 'EXCEL', label: 'Excel (без джерела)' },
  { value: 'BAS', label: 'BAS' },
];

// Які сутності залежать від organization_id. Дзеркало backend моделі:
// використовуємо NEEDS_ORG_SELECTOR з api/openingBalances як SSOT.
function needsOrg(k: Kind): boolean {
  return (NEEDS_ORG_SELECTOR as readonly string[]).includes(k);
}

// Сутності, для яких backend ще не реалізував write (501-stub).
// employees — двофазний імпорт з individuals, окрема порція (TECHDEBT).
const DISABLED_KINDS: Kind[] = ['employees'];

export default function ImportFromCsvPage() {
  const [kind, setKind] = useState<Kind>('contractors');
  const [source, setSource] = useState('1C_BP');
  const [helpOpen, setHelpOpen] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [rawText, setRawText] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Б.5: SPEC 4b умова — підтвердження перезапису ДО реального POST.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);
  }, [orgList.data, orgId]);

  const cols = COLS[kind];

  function autoMap(hdr: string[]) {
    const auto: Record<string, number> = {};
    for (const col of cols) {
      const idx = hdr.findIndex((h) =>
        h.toLowerCase().includes(col.key.toLowerCase()) ||
        h.toLowerCase() === col.label.toLowerCase().split(' ')[0]
      );
      if (idx >= 0) auto[col.key] = idx;
    }
    return auto;
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then((t) => {
      setRawText(t);
      const parsed = parseCsv(t);
      if (parsed.length === 0) { setError('Порожній файл'); return; }
      const hdr = parsed[0]!;
      setHeaders(hdr);
      setRows(parsed.slice(1));
      setMapping(autoMap(hdr));
      setError(null);
      setResult(null);
    });
  }

  function pasteFromText(t: string) {
    setRawText(t);
    const parsed = parseCsv(t);
    if (parsed.length === 0) return;
    const hdr = parsed[0]!;
    setHeaders(hdr);
    setRows(parsed.slice(1));
    setMapping(autoMap(hdr));
  }

  function onKindChange(k: Kind) {
    setKind(k);
    setMapping({});
    setResult(null);
  }

  const previewRows = useMemo(() => rows.slice(0, 10), [rows]);

  // Рядки з заповненим external_id потенційно стануть UPDATE (upsert
  // знайде існуючі у межах owner). Це грубий прогноз — реальний
  // підрахунок робить backend. Використовується у ConfirmImportDialog.
  const rowsWithExternalIdCount = useMemo(() => {
    const extIdx = mapping['external_id'];
    if (extIdx === undefined || extIdx < 0) return 0;
    return rows.filter((r) => (r[extIdx] ?? '').trim() !== '').length;
  }, [rows, mapping]);

  function cell(row: string[], key: string): string | undefined {
    const idx = mapping[key];
    if (idx === undefined || idx < 0) return undefined;
    const v = row[idx];
    return v === '' ? undefined : v;
  }
  const bool = (v: string | undefined): boolean | undefined => {
    if (v === undefined) return undefined;
    const lv = v.toLowerCase();
    if (lv === 'true' || lv === '1' || lv === 'так' || lv === 'да') return true;
    if (lv === 'false' || lv === '0' || lv === 'ні' || lv === 'нет') return false;
    return undefined;
  };

  function buildPayload(): unknown[] {
    if (kind === 'contractors') {
      return rows.map((row): ContractorImportRow => ({
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? null,
        name: cell(row, 'name') ?? '',
        full_name: cell(row, 'full_name') ?? null,
        contractor_type: (cell(row, 'contractor_type') as 'LEGAL' | undefined) ?? 'LEGAL',
        edrpou: cell(row, 'edrpou') ?? null,
        inn: cell(row, 'inn') ?? null,
        is_vat_payer: bool(cell(row, 'is_vat_payer')) ?? false,
        vat_number: cell(row, 'vat_number') ?? null,
        is_buyer: true, is_supplier: true, is_other: false,
      }));
    }
    if (kind === 'nomenclature') {
      return rows.map((row): NomenclatureImportRow => ({
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? null,
        article: cell(row, 'article') ?? null,
        name: cell(row, 'name') ?? '',
        full_name: cell(row, 'full_name') ?? null,
        item_type: (cell(row, 'item_type') as 'GOOD' | 'SERVICE' | undefined) ?? 'GOOD',
      }));
    }
    if (kind === 'departments') {
      // Б.2: organization_id у row ЗАБОРОНЕНО (extra='forbid' 422).
      // owner → target_organization_id у query (orgId).
      return rows.map((row): DepartmentImportRow => ({
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? '',
        name: cell(row, 'name') ?? '',
        parent_external_id: cell(row, 'parent_external_id') ?? null,
      }));
    }
    if (kind === 'positions') {
      return rows.map((row): PositionImportRow => ({
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? '',
        name: cell(row, 'name') ?? '',
        kp_code: cell(row, 'kp_code') ?? null,
      }));
    }
    // employees — disabled у UI, але buildPayload існує для типу.
    // Б.2 контракт без organization_id per-row.
    return rows.map((row): EmployeeImportRow => ({
      external_id: cell(row, 'external_id') ?? null,
      last_name: cell(row, 'last_name') ?? '',
      first_name: cell(row, 'first_name') ?? '',
      middle_name: cell(row, 'middle_name') ?? null,
      inn: cell(row, 'inn') ?? null,
      code: cell(row, 'code') ?? null,
      position_external_id: cell(row, 'position_external_id') ?? null,
      department_external_id: cell(row, 'department_external_id') ?? null,
      hire_date: cell(row, 'hire_date') ?? null,
      is_main_workplace: true,
    }));
  }

  // Б.5: SPEC 4b умова — ДО реального write показати користувачу
  // ОЧІКУВАНИЙ масштаб перезапису (скільки буде створено / оновлено
  // з очищенням полів). Підтверджується явним кліком.
  function validateBeforeConfirm(): boolean {
    setError(null);
    setResult(null);
    if (DISABLED_KINDS.includes(kind)) {
      setError(
        'Імпорт співробітників тимчасово недоступний — потребує спарованого імпорту з фізособами (окрема порція). Backend поверне 501.',
      );
      return false;
    }
    if (rows.length === 0) { setError('Немає даних для імпорту'); return false; }
    const requiredKeys = cols.filter((c) => c.required).map((c) => c.key);
    for (const k of requiredKeys) {
      if (mapping[k] === undefined || mapping[k] === -1) {
        setError(`Не задана колонка для обов'язкового поля "${cols.find((c) => c.key === k)?.label}"`);
        return false;
      }
    }
    if (needsOrg(kind) && !orgId) {
      setError("Оберіть організацію (вона є обов'язковою для цього довідника)");
      return false;
    }
    // Базова перевірка зовнішніх ID для оцінки масштабу update'у.
    // Реальний підрахунок робить backend — UI лише показує, що
    // upsert per-owner означає «перезапис існуючих».
    return true;
  }

  async function runImport() {
    if (!validateBeforeConfirm()) return;

    setLoading(true);
    try {
      const payload = buildPayload();
      // owner = orgId для departments/employees; для решти — backend
      // візьме tenant_id з контексту, target_organization_id просто
      // підтверджує доступ scope.assert_access. Передаємо orgId або
      // перший доступний org (бо параметр обов'язковий завжди).
      const effectiveOrg = orgId || (orgList.data?.items[0]?.id ?? '');
      if (!effectiveOrg) {
        setError('Немає доступної організації для цього клієнта.');
        return;
      }
      let res: ImportResult;
      if (kind === 'contractors')
        res = await imports.contractors(effectiveOrg, source, payload as ContractorImportRow[]);
      else if (kind === 'nomenclature')
        res = await imports.nomenclature(effectiveOrg, source, payload as NomenclatureImportRow[]);
      else if (kind === 'departments')
        res = await imports.departments(effectiveOrg, source, payload as DepartmentImportRow[]);
      else if (kind === 'positions')
        res = await imports.positions(effectiveOrg, source, payload as PositionImportRow[]);
      else
        res = await imports.employees(effectiveOrg, source, payload as EmployeeImportRow[]);
      setResult(res);
      setConfirmOpen(false);
    } catch (e) {
      setError(humanizeBackendError(e));
      setConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Імпорт довідників з CSV</h2>
        <button onClick={() => setHelpOpen(true)}>
          ❓ Як імпортувати?
        </button>
      </div>

      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '1rem', borderBottom: '2px solid #e2e8f0' }}>
        {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
          <button
            key={k}
            onClick={() => onKindChange(k)}
            style={{
              padding: '0.5rem 1rem',
              background: kind === k ? '#3b82f6' : '#f1f5f9',
              color: kind === k ? 'white' : '#475569',
              border: 'none',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: kind === k ? 600 : 400,
            }}
          >
            {KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
        Експортуйте з 1С у файл CSV. Перший рядок — назви колонок.
        Підтримуються роздільники <code>;</code> або <code>,</code>.
      </p>

      {kind === 'employees' && (
        <div style={{
          padding: '0.75rem', background: '#fef3c7',
          border: '1px solid #fcd34d', borderRadius: 'var(--radius-sm)',
          marginBottom: '1rem',
        }}>
          <strong>⚠ Імпорт співробітників тимчасово недоступний</strong>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
            Потребує спарованого імпорту з фізособами (двофазна операція з
            обробкою персональних даних) — окрема порція з власною
            специфікацією. Поки використовуйте ручне створення на сторінці{' '}
            <a href="/employees">Співробітники</a>.
          </p>
        </div>
      )}

      <div style={{
        display: 'grid',
        // Б.5: усі 5 ендпойнтів потребують source (1С/Excel/BAS).
        // Селектор org — лише для departments/employees (needsOrg).
        gridTemplateColumns: needsOrg(kind) ? '1fr 1fr 1fr' : '1fr 1fr',
        gap: '1rem', marginBottom: '1rem'
      }}>
        <div className="field">
          <label>Джерело</label>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {SOURCE_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        {needsOrg(kind) && (
          <div className="field">
            <label>Організація</label>
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">—</option>
              {orgList.data?.items.map((o) => (
                <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label>Файл CSV</label>
          <input type="file" accept=".csv,.txt" onChange={onFile} />
        </div>
      </div>

      <details style={{ marginBottom: '1rem' }}>
        <summary style={{ cursor: 'pointer', color: '#475569' }}>
          або вставити дані з буфера (Ctrl+V вмісту з Excel)
        </summary>
        <textarea
          value={rawText}
          onChange={(e) => pasteFromText(e.target.value)}
          rows={6}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem', marginTop: '0.3rem' }}
          placeholder="ID;Назва;..."
        />
      </details>

      {headers.length > 0 && (
        <>
          <h3 style={{ marginTop: '1rem' }}>Прив'язка колонок</h3>
          <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
            Знайдено {headers.length} колонок, {rows.length} рядків. Виберіть для кожного поля джерело:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
            {cols.map((col) => (
              <div key={col.key} className="field" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '0.8rem' }}>
                  {col.label}
                  {col.required && <span style={{ color: '#dc2626' }}> *</span>}
                </label>
                <select
                  value={mapping[col.key] ?? ''}
                  onChange={(e) => setMapping({ ...mapping, [col.key]: e.target.value === '' ? -1 : Number(e.target.value) })}
                >
                  <option value="">— не імпортувати —</option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>{h || `Колонка ${i + 1}`}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <h3 style={{ marginTop: '1rem' }}>Попередній перегляд (перші 10)</h3>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {headers.map((h, i) => (
                    <th key={i} style={{ padding: '0.3rem 0.5rem', textAlign: 'left', border: '1px solid #e2e8f0' }}>
                      {h || `Кол. ${i + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {row.map((c, j) => (
                      <td key={j} style={{ padding: '0.2rem 0.5rem', border: '1px solid #e2e8f0' }}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <button
              className="primary"
              onClick={() => {
                if (validateBeforeConfirm()) setConfirmOpen(true);
              }}
              disabled={loading}
            >
              {loading ? 'Імпортуємо…' : `▶ Імпортувати ${rows.length} рядків`}
            </button>
          </div>
        </>
      )}

      {/* Б.5 SPEC 4b: підтвердження перезапису ДО реального POST */}
      {confirmOpen && (
        <ConfirmImportDialog
          kind={kind}
          rowCount={rows.length}
          rowsWithExternalId={rowsWithExternalIdCount}
          orgName={orgList.data?.items.find((o) => o.id === orgId)?.name ?? ''}
          loading={loading}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={runImport}
        />
      )}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}

      {result && (
        <div style={{
          marginTop: '1rem', padding: '1rem',
          background: result.errors.length === 0 ? '#dcfce7' : '#fef3c7',
          borderRadius: 6,
        }}>
          <strong>Результат:</strong>
          <ul style={{ margin: '0.5rem 0' }}>
            <li>Створено нових: <strong>{result.inserted}</strong></li>
            <li>Оновлено існуючих: <strong>{result.updated}</strong></li>
            <li>Пропущено: <strong>{result.skipped}</strong></li>
          </ul>
          {result.errors.length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer' }}>Помилок: {result.errors.length}</summary>
              <ul style={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                {result.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </>
  );
}

// ===================== HelpModal =====================

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="drawer" style={{ width: 'min(720px, 100vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Як імпортувати довідник — покрокова інструкція</h2>
          <button onClick={onClose}>✕</button>
        </div>

        <p style={{ color: 'var(--text-secondary)' }}>
          Імпорт — це завантаження даних з вашого Excel-файла (або CSV)
          у цю систему. Усе працює у вашому браузері: вибираєте файл,
          вказуєте які колонки що означають, тиснете «Імпортувати».
        </p>

        <Section title="Крок 0. Вивантаження з 1С в Excel">
          <p>
            <strong>Якщо у вас 1С: БП / УТП / УТ для України</strong> — спочатку
            треба отримати дані у вигляді Excel-таблиці. Є три способи —
            від найпростішого до найдетальнішого.
          </p>

          <h4 style={{ margin: '0.75rem 0 0.3rem', fontSize: '0.95rem' }}>
            Спосіб 1 (найпростіший) — кнопка «Вивести список»
          </h4>
          <ol>
            <li>Відкрийте потрібний довідник у 1С:
              <ul>
                <li><em>Контрагенти</em>: «Довідники → Контрагенти»</li>
                <li><em>Номенклатура</em>: «Довідники → Номенклатура»</li>
                <li><em>Співробітники</em>: «Довідники → Співробітники
                  організацій»</li>
                <li><em>Підрозділи</em>: «Довідники → Підрозділи організацій»</li>
                <li><em>Посади</em>: «Довідники → Посади»</li>
              </ul>
            </li>
            <li>
              Над списком оберіть колонки, які потрібно вивантажити
              (правий клік на шапці → «Налаштування списку» → відмітьте
              потрібні поля: <em>Код, Найменування, ЄДРПОУ, ІПН, Повне
              найменування</em> тощо).
            </li>
            <li>
              Правий клік у списку → <strong>«Вивести список…»</strong>
              (або меню «Усі дії → Вивести список»).
            </li>
            <li>
              У діалозі оберіть тип виводу <strong>«Табличний документ»</strong> →
              ОК.
            </li>
            <li>
              У вікні, що відкриється, меню&nbsp;
              <strong>«Файл → Зберегти як…»</strong> →&nbsp;
              тип файлу <strong>«Аркуш Excel (*.xls)»</strong> або
              <strong>«*.xlsx»</strong> → збережіть.
            </li>
          </ol>
          <Hint>
            💡 Спосіб простий, але вивантажує лише ті колонки, що видно
            на екрані. ІПН, ЄДРПОУ, банківські рахунки треба попередньо
            додати у налаштуваннях списку.
          </Hint>

          <h4 style={{ margin: '0.75rem 0 0.3rem', fontSize: '0.95rem' }}>
            Спосіб 2 — універсальний звіт «Список»
          </h4>
          <ol>
            <li>«Звіти → Універсальний звіт» (або «Список»).</li>
            <li>У полі <em>«Тип об'єкта»</em> — оберіть «Довідник»,
              у <em>«Об'єкт»</em> — назву довідника
              (Контрагенти / Номенклатура / тощо).</li>
            <li>Додайте всі потрібні поля через «Налаштування → Поля та сортування».
              Обов'язково додайте поле <strong>«Посилання»</strong>
              (це системний ID 1С) — воно стане вашим «ID з 1С» в нашій системі
              і дозволить потім робити повторні імпорти без дублів.</li>
            <li>Натисніть <strong>«Сформувати»</strong>.</li>
            <li>«Файл → Зберегти як → Аркуш Excel».</li>
          </ol>

          <h4 style={{ margin: '0.75rem 0 0.3rem', fontSize: '0.95rem' }}>
            Спосіб 3 — обробка «Завантаження/Вивантаження даних XML»
          </h4>
          <p>
            Стандартна обробка 1С для перенесення даних між базами.
            Вивантажує все одразу разом з усіма полями. Використовуйте
            якщо «Вивести список» не дає потрібного результату.
          </p>
          <ol>
            <li>«Сервіс → Завантаження/Вивантаження даних → Вивантажити дані».</li>
            <li>Оберіть потрібні об'єкти.</li>
            <li>Отримаєте XML — його треба перетворити в Excel
              (вручну або через спеціальні обробки).</li>
          </ol>

          <h4 style={{ margin: '0.75rem 0 0.3rem', fontSize: '0.95rem' }}>
            Очистіть Excel перед збереженням у CSV
          </h4>
          <ol>
            <li>
              Видаліть «зайві» рядки 1С зверху — заголовки звіту, дату
              формування, назву організації. Має лишитися <strong>тільки
              шапка колонок і самі дані</strong>.
            </li>
            <li>
              Перевірте, що колонки <strong>ІПН і ЄДРПОУ</strong> мають
              формат «Текст» (правий клік → «Формат комірок → Текст»).
              Інакше Excel «обріже» 10-значний ЄДРПОУ у вигляді
              <code>1.23E+09</code>.
            </li>
            <li>
              Перейменуйте колонки на латиницю, якщо хочете щоб система
              автоматично їх розпізнала: <em>name, code, edrpou, inn,
              full_name</em> (не обов'язково — потім вручну все одно мапимо).
            </li>
            <li>
              Збережіть як <strong>CSV (роздільники крапки з комою)</strong>
              у кодуванні <strong>UTF-8</strong> — дивіться Крок 1.
            </li>
          </ol>
          <Hint>
            ⚠️ Якщо у вас тисячі контрагентів — вивантажуйте окремо
            <strong> постачальників і покупців</strong>: у 1С є відповідні
            галочки-фільтри. Так легше перевірити дані перед імпортом.
          </Hint>
        </Section>

        <Section title="Крок 1. Підготуйте файл в Excel">
          <ol>
            <li>Відкрийте Excel-файл з вашими даними (контрагенти,
              номенклатура, співробітники тощо).</li>
            <li>
              <strong>Перший рядок</strong> має бути «шапкою» з назвами колонок
              (наприклад: <em>Код, Назва, ЄДРПОУ, ІПН</em>).
              Решта рядків — самі дані.
            </li>
            <li>
              Збережіть файл як <strong>CSV (роздільники-крапки з комою)</strong>:
              <br />
              <em>Файл → Зберегти як → Тип файлу:&nbsp;</em>
              <code>CSV (роздільники — крапки з комою) (*.csv)</code>
            </li>
            <li>
              Якщо у вас Mac/LibreOffice — підійде звичайний CSV
              з комою або крапкою з комою — система розпізнає обидва автоматично.
            </li>
          </ol>
          <Hint>
            ⚠️ Кодування — UTF-8. Якщо в LibreOffice — при збереженні поставте
            галочку «Edit filter settings» і виберіть UTF-8.
            Якщо в Excel збереглось у Win-1251 і кирилиця «псується» —
            відкрийте CSV у Блокноті, оберіть «Зберегти як» → Кодування UTF-8.
          </Hint>
        </Section>

        <Section title="Крок 2. Виберіть тип довідника">
          <p>Зверху на сторінці — вкладки:</p>
          <ul>
            <li><strong>Контрагенти</strong> — постачальники, покупці, фізособи.</li>
            <li><strong>Номенклатура</strong> — товари і послуги.</li>
            <li><strong>Відділи</strong> — підрозділи (для кадрів).</li>
            <li><strong>Посади</strong> — посади (для кадрів).</li>
            <li><strong>Співробітники</strong> — повністю готові картки
              з ІПН, прийомом на роботу тощо.</li>
            <li><strong>Класифікатор професій</strong> — офіційний ДК 003:2010
              (вже завантажений, можна оновити).</li>
          </ul>
        </Section>

        <Section title="Крок 3. Вкажіть джерело і організацію (якщо треба)">
          <ul>
            <li>
              <strong>Джерело</strong> — звідки ви брали дані (1С: БП, 1С: УТ,
              Excel і т. п.). Це потрібно, щоб система могла потім
              синхронізувати, якщо ви оновите цей же файл (UPSERT по «ID з 1С»).
            </li>
            <li>
              <strong>Організація</strong> — обов'язкова для довідників,
              що прив'язані до конкретної юр.особи (відділи, співробітники).
              Не потрібна для контрагентів і номенклатури.
            </li>
          </ul>
        </Section>

        <Section title="Крок 4. Завантажте файл">
          <ol>
            <li>Натисніть кнопку <strong>«Обрати файл»</strong> і виберіть
              підготовлений CSV.</li>
            <li>З'явиться попередній перегляд: перші 5 рядків, як вони розпізналися.</li>
          </ol>
        </Section>

        <Section title="Крок 5. Замапіть колонки">
          <p>
            Система не знає заздалегідь, яка ваша колонка означає «Назва»,
            а яка — «Код». Тому під кожним полем системи (наприклад, «Назва»,
            «ЄДРПОУ», «ІПН») є випадаючий список — оберіть колонку
            з вашого файла.
          </p>
          <Hint>
            💡 Поля з зірочкою <strong>*</strong> — обов'язкові.
            Решту можна залишити порожніми, якщо у файлі таких даних немає.
          </Hint>
          <p>
            Часто система сама вгадує колонки, якщо назви в шапці очевидні
            («Назва», «name», «Код», «code», «ІПН», «inn» тощо).
            Перевірте і поправте при потребі.
          </p>
        </Section>

        <Section title="Крок 6. Запустіть імпорт">
          <ol>
            <li>Натисніть <strong>«Імпортувати»</strong> — велика синя кнопка.</li>
            <li>
              За кілька секунд (для тисяч рядків — кілька хвилин) з'явиться
              результат:
              <ul>
                <li><strong>inserted</strong> — створено нових записів</li>
                <li><strong>updated</strong> — оновлено існуючих
                  (якщо у файлі є «ID з 1С» і запис вже був у системі)</li>
                <li><strong>skipped</strong> — пропущено через помилки</li>
                <li><strong>errors</strong> — деталі по перших 50 помилках</li>
              </ul>
            </li>
            <li>
              Якщо є <strong>skipped</strong> — почитайте текст помилок,
              виправте у файлі і повторіть. Записи, що вже залились,
              <strong>не дублюватимуться</strong> (UPSERT по «ID з 1С»).
            </li>
          </ol>
        </Section>

        <Section title="Порядок імпорту для кадрів">
          <p>Якщо ви заводите кадри з нуля, дотримуйтеся послідовності:</p>
          <ol>
            <li><strong>Відділи</strong> (Підрозділи) — спочатку,
              щоб співробітники могли на них посилатися.</li>
            <li><strong>Посади</strong> — можна одночасно з відділами,
              вони незалежні.</li>
            <li><strong>Класифікатор професій</strong> — якщо ще не залитий
              (для зв'язку з КП-кодами).</li>
            <li><strong>Співробітники</strong> — останніми, в CSV вкажіть
              <em>position_external_id</em> і <em>department_external_id</em>
              (зовнішні коди з 1С). Система знайде відповідні відділи/посади
              автоматично.</li>
          </ol>
        </Section>

        <Section title="Типові помилки">
          <ul>
            <li>
              <strong>«Кирилиця псується, замість літер ?»</strong> —
              файл не в UTF-8. Пересохраніть у Блокноті: «Зберегти як» →
              Кодування UTF-8.
            </li>
            <li>
              <strong>«Колонки не розпізнаються»</strong> — система очікує,
              що перший рядок це шапка. Перевірте, що ви не видалили шапку.
            </li>
            <li>
              <strong>«Все в одну колонку»</strong> — Excel зберіг з комами,
              а у вас точки з комою (або навпаки). Спробуйте інший роздільник
              у «Зберегти як» або відкрийте файл в текстовому редакторі і
              перевірте.
            </li>
            <li>
              <strong>«ІПН/ЄДРПОУ як число у науковій формі (1.23E+09)»</strong> —
              Excel «обрізав» довге число. У файлі-джерелі поставте формат
              «Текст» для цих колонок перед збереженням у CSV.
            </li>
            <li>
              <strong>«duplicate key»</strong> — у файлі є дублі по коду/ЄДРПОУ.
              Видаліть дублі або користуйтеся колонкою «ID з 1С»
              (тоді буде UPDATE, не INSERT).
            </li>
          </ul>
        </Section>

        <Section title="Що буде з даними?">
          <p>
            Дані потрапляють в довідники системи — далі їх можна
            використовувати у документах (видаткові, накладні, нарахування ЗП
            тощо). Перевірити що залилось можна на сторінках:
            <br />
            <a href="/contractors">Контрагенти</a>,&nbsp;
            <a href="/nomenclature">Номенклатура</a>,&nbsp;
            <a href="/departments">Підрозділи</a>,&nbsp;
            <a href="/positions">Посади</a>,&nbsp;
            <a href="/employees">Співробітники</a>.
          </p>
        </Section>

        <div className="drawer__actions">
          <button className="primary" onClick={onClose}>Зрозуміло</button>
        </div>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <details open style={{
      margin: '0.75rem 0', padding: '0.5rem 0.75rem',
      background: 'var(--bg-soft-2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
    }}>
      <summary style={{ fontWeight: 700, cursor: 'pointer', padding: '0.25rem 0' }}>
        {title}
      </summary>
      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem',
                    color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        {children}
      </div>
    </details>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--warning-soft)', border: '1px solid #fde68a',
      borderRadius: 'var(--radius-sm)', padding: '0.4rem 0.7rem',
      fontSize: '0.85rem', margin: '0.5rem 0',
    }}>
      {children}
    </div>
  );
}


/**
 * Б.5 SPEC 4b: ОБОВ'ЯЗКОВЕ підтвердження перезапису.
 *
 * Backend робить upsert ПОВНОЮ перезаписом: при існуючому external_id
 * усі поля з файлу замінюють поточні, відсутні у файлі поля очищуються
 * до NULL. Для бухгалтерського продукту тиха втрата введеного вручну
 * = втрата довіри. Тому ДО POST показуємо очікуваний масштаб.
 *
 * Прогноз inserted/updated — грубий (з frontend, без БД lookup): рядки
 * з заповненим external_id потенційно update'и, без нього — точно
 * insert. Точні числа повертає backend після POST.
 */
function ConfirmImportDialog({
  kind, rowCount, rowsWithExternalId, orgName, loading, onCancel, onConfirm,
}: {
  kind: Kind;
  rowCount: number;
  rowsWithExternalId: number;
  orgName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const newGuess = rowCount - rowsWithExternalId;
  const updateGuess = rowsWithExternalId;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', padding: '1.5rem', borderRadius: 'var(--radius)',
          width: '540px', maxWidth: '90vw',
        }}
      >
        <h3 style={{ marginTop: 0 }}>Підтвердження імпорту</h3>

        <p>
          Сутність: <strong>{KIND_LABELS[kind]}</strong>
          {orgName && <> &middot; Юрособа: <strong>{orgName}</strong></>}
        </p>

        <p>Очікуваний масштаб операції:</p>
        <ul style={{ lineHeight: 1.7 }}>
          <li>
            ≈ <strong>{newGuess}</strong>{' '}
            {newGuess === 1 ? 'рядок буде створений' : 'рядків буде створено'}{' '}
            <span style={{ color: 'var(--text-secondary)' }}>(без external_id)</span>
          </li>
          <li>
            ≈ <strong>{updateGuess}</strong>{' '}
            {updateGuess === 1 ? 'рядок буде оновлений' : 'рядків буде оновлено'}{' '}
            <span style={{ color: 'var(--text-secondary)' }}>(з external_id, якщо вже є у БД)</span>
          </li>
        </ul>

        <div
          style={{
            marginTop: '1rem', padding: '0.75rem', background: '#fef3c7',
            border: '1px solid #fcd34d', borderRadius: 'var(--radius-sm)',
          }}
        >
          <strong>⚠ Повний перезапис при оновленні</strong>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem' }}>
            Поля, які <strong>відсутні у файлі</strong>, будуть{' '}
            <strong>очищені до пустого значення</strong> у існуючих записах.
            Введене вручну у системі буде втрачене для цих полів.
            Цей імпорт <strong>атомарний</strong>: якщо хоча б один рядок
            помилковий — нічого не запишеться.
          </p>
        </div>

        <div
          style={{
            marginTop: '1rem', display: 'flex',
            justifyContent: 'flex-end', gap: '0.5rem',
          }}
        >
          <button type="button" onClick={onCancel} disabled={loading}>
            Скасувати
          </button>
          <button
            type="button"
            className="primary"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Імпортуємо…' : `Підтверджую імпорт ${rowCount} рядків`}
          </button>
        </div>
      </div>
    </div>
  );
}
